import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import type {
  DtoV1BoltAuditBoltAuditEntity,
  DtoV1BoltAuditBoltAuditRequest,
  DtoV1BoltAuditBoltAuditResponse,
  DtoV1BoltAuditBoltAuditSummaryScreenResponse,
  DtoV1BoltAuditBorrower1003AuditPageResponse,
  DtoV1BoltAuditBorrower1003EntityAudit,
  DtoV1BoltAuditEntityFieldResponse,
  DtoV1BoltAuditFieldAudit,
  DtoV1EnumBoltAuditPage,
  DtoV1EnumEntityType,
  KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse,
} from '~/__generated__/api';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { auditHistoryDataReducer } from '~/common/util/auditHistory';
import { toTitleCase } from '~/common/util/format';

import {
  AuditHistoryEnityEnum,
  AuditHistoryPageEnum,
  getCategoryName,
} from './constants';
import {
  transformBoltAuditEndpointData,
  transformSectionedBoltAuditEndpointData,
} from './utils';

export function useGetAssetAuditHistory(assetId, assetTypeId) {
  const { obfuscatedLoanIdentifier } = useUrl();
  return useQuery({
    queryKey: ['getAssetAuditHistoryData', assetId],
    queryFn: async () => {
      const { data } = await typedApiClient.v1.boltAuditGetAssetDepositoryAudit(
        {
          obfuscatedLoanIdentifier,
          assetId: +assetId,
          assetTypeId: +assetTypeId,
        },
      );

      // this is subject to change, for the POC/Prototype this seemed to be good enough
      return data.entity;
    },
  });
}

export function useGetAuditHistory(
  pageEnum: string,
  id: string,
  entityEnum: string,
) {
  const [searchParams, _setSearchParams] = useSearchParams();
  const { obfuscatedLoanIdentifier } = useUrl();
  return useQuery({
    queryKey: ['audit-history', pageEnum, id, searchParams.toString()],
    queryFn: async () => {
      const requestBody: DtoV1BoltAuditBoltAuditRequest = {
        obfuscatedLoanIdentifier,
        page: Number.parseInt(pageEnum) as DtoV1EnumBoltAuditPage,
      };

      let entityReferenceAssociations: DtoV1BoltAuditBoltAuditRequest['entityReferenceAssociations'] =
        [];

      const ENTITY_REFERENCE_FIELD_MAP = {
        [AuditHistoryEnityEnum.BORROWER.toString()]: 'customerRecordId',
        [AuditHistoryEnityEnum.ASSET.toString()]: 'assetRecordId',
        [AuditHistoryEnityEnum.ASSET_GIFT_GOE_ENTITY.toString()]:
          'assetRecordId',
      };
      const referenceField = ENTITY_REFERENCE_FIELD_MAP[entityEnum];
      if (referenceField) {
        entityReferenceAssociations = [
          {
            entity: Number.parseInt(entityEnum) as DtoV1EnumEntityType,
            references: {
              [referenceField]: id,
            },
          },
        ];
      }

      if (searchParams?.size) {
        const allQueryParams = Object.fromEntries(searchParams.entries());
        entityReferenceAssociations.push({
          references: {
            ...allQueryParams,
          },
        });
      }

      if (entityReferenceAssociations.length) {
        requestBody.entityReferenceAssociations = entityReferenceAssociations;
      }

      const { data } =
        await typedApiClient.v1.boltAuditGetBoltAudit(requestBody);

      const transformedData = transformBoltAuditEndpointData(data);

      // this is subject to change, for the POC/Prototype this seemed to be good enough
      return transformedData.entitiesAudit;
    },
  });
}

export function useGetSectionedAuditHistory(
  pageEnum: string,
  entityEnum: string,
  references: Record<string, string>,
) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: [
      'audit-history',
      'getSectionedAuditHistoryData',
      { entityEnum, pageEnum, references },
    ],
    queryFn: async () => {
      const requestBody: DtoV1BoltAuditBoltAuditRequest = {
        obfuscatedLoanIdentifier,
        page: Number.parseInt(pageEnum) as DtoV1EnumBoltAuditPage,
      };

      if (entityEnum) {
        requestBody.entityReferenceAssociations = [
          {
            entity: Number.parseInt(entityEnum) as DtoV1EnumEntityType,
            references: references,
          },
        ];
      }

      const { data } =
        await typedApiClient.v1.boltAuditGetSectionedBoltAudit(requestBody);
      return data;
    },
  });
}

export function useLegacyAuditHistory(
  ruleIds: string[],
  ruleReferenceIdentifiers: Array<{
    ruleReferenceType?: any;
    lenderDatabaseId?: number;
    ruleReferenceValue?: string;
  }>,
  auditHistoryTypes: number[],
  questionCategory: string,
) {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: ['audit-history-btn', ruleIds],
    async queryFn() {
      const {
        data: { response },
      } = await typedApiClient.v1.boltHistoryGetBoltAuditHistory({
        obfuscatedLoanIdentifier,
        auditHistoryTypes,
        ruleReferenceIdentifiers,
      });
      return auditHistoryDataReducer(response, questionCategory);
    },
    cacheTime: 0,
    enabled: false,
  });
}

export function useGetValidateDocumentAuditHistory(
  customerRecordId: number | string,
  employerId: number | string,
  documentId: number | string,
) {
  const { obfuscatedLoanIdentifier } = useUrl();
  const incomeIdentifier = {
    obfuscatedLenderDatabaseId:
      obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
    customerRecordId: +customerRecordId,
    employerId: +employerId,
    sequenceNumber: 0,
  };

  return useQuery({
    queryKey: [
      'audit-history',
      'getValidateDocumentAuditHistoryData',
      documentId,
    ],
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.boltAuditGetValidateDocumentAudit({
          obfuscatedLoanIdentifier,
          incomeIdentifier: incomeIdentifier,
          documentId: +documentId,
        });

      // this is subject to change based on document type
      return data.entity;
    },
  });
}

export function useGetIncomeAuditHistory(
  boltAuditPage,
  entity,
  applicableIncomeAuditId,
  applicableIncomeAuditType,
  employerId,
  customerRecordId,
  lenderDatabaseId,
) {
  const { obfuscatedLoanIdentifier } = useUrl();

  const references: Record<string, string> = {
    EmployerId: employerId,
    CustomerRecordId: customerRecordId,
    CustomerLenderDatabaseId: lenderDatabaseId,
  };

  if (entity === AuditHistoryEnityEnum.INCOME_DOCUMENT.toString()) {
    references.DocumentId = applicableIncomeAuditId;
    references.DocumentType = applicableIncomeAuditType;
  } else if (entity === AuditHistoryEnityEnum.INCOME_CALCULATION.toString()) {
    references.CalculationId = applicableIncomeAuditId;
    references.CalculatorType = applicableIncomeAuditType;
  }

  const entityReferenceAssociations = [
    {
      entity: entity,
      references: references,
    },
  ];
  const queryKey = [
    'audit-history',
    boltAuditPage == AuditHistoryPageEnum.INCOME_RECONCILIATION
      ? 'getIncomeReconAuditHistory'
      : 'getIncomeAuditHistory',
    boltAuditPage,
  ];

  const queryFn = async () => {
    if (boltAuditPage == AuditHistoryPageEnum.INCOME_RECONCILIATION) {
      const { data } =
        await typedApiClient.v1.boltAuditIncomeReconciliationList(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        );
      return data.entity;
    } else {
      const { data } = await typedApiClient.v1.boltAuditGetIncomeAuditHistory({
        obfuscatedLoanIdentifier,
        page: boltAuditPage,
        entityReferenceAssociations: entityReferenceAssociations,
      });
      return data.entity;
    }
  };

  return useQuery({ queryKey, queryFn });
}

export function useGetIncomeCalculatorAuditHistory(
  customerRecordId: number,
  employerId: number,
  calculatorTypeId: string | number,
) {
  const { obfuscatedLoanIdentifier } = useUrl();
  const incomeIdentifier = {
    obfuscatedLenderDatabaseId:
      obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
    customerRecordId: customerRecordId,
    employerId: employerId,
    sequenceNumber: 0,
  };

  return useQuery({
    queryKey: [
      'audit-history',
      'getIncomeCalculatorAuditHistoryData',
      calculatorTypeId,
    ],
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.boltAuditGetIncomeCalculatorAudit({
          obfuscatedLoanIdentifier,
          incomeIdentifier: incomeIdentifier,
          calculatorTypeId: +calculatorTypeId,
        });

      // this is subject to change based on document type
      return data.entity;
    },
  });
}

export function useGetTransferReconciliationAudit() {
  const { obfuscatedLoanIdentifier } = useUrl();
  return useQuery({
    queryKey: [
      'audit-history',
      'getTransferReconciliationAudit',
      obfuscatedLoanIdentifier,
    ],
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.boltAuditGetTransferReconciliationAudit(
          obfuscatedLoanIdentifier,
        );

      return data.entity;
    },
  });
}

export function useGetAssetReconciliationAudit() {
  const { obfuscatedLoanIdentifier } = useUrl();
  return useQuery({
    queryKey: [
      'audit-history',
      'getAssetReconciliationAudit',
      obfuscatedLoanIdentifier,
    ],
    queryFn: async () => {
      const { data } = await typedApiClient.v1.boltAuditAssetReconciliationList(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
      );

      return data.entity;
    },
  });
}

export function useGetDepositSourcingAudit(assetId) {
  const { obfuscatedLoanIdentifier } = useUrl();
  return useQuery({
    queryKey: [
      'audit-history',
      'getDepositSourcingAudit',
      obfuscatedLoanIdentifier,
    ],
    queryFn: async () => {
      const { data } = await typedApiClient.v1.boltAuditDepositSourcingList(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        assetId,
      );

      return data.entity;
    },
  });
}

export function useGetAuditHistoryDynamicTabs(
  page: string,
  pageEnum?: string,
  category?: string,
) {
  // Define NormalizedAuditEntity type
  type NormalizedAuditEntity = {
    tab?: string;
    sectionTitle: string;
    entityFields: any[];
    entityDisplayName?: string;
    entitySectionTitle?: string;
    deletedByDisplayName?: string;
    useAccordions: boolean;
    isActiveDeletedTab?: boolean;
  };

  const { obfuscatedLoanIdentifier } = useUrl();

  const queryPageKeys = {
    'status-and-review': [
      'audit-history',
      'statusReviewAuditHistory',
      obfuscatedLoanIdentifier,
    ],
    'summary-data': [
      'audit-history',
      'getAuditHistorySummaryData',
      obfuscatedLoanIdentifier,
    ],
    borrower1003: [
      'audit-history',
      'getAuditHistoryBorrower1003',
      obfuscatedLoanIdentifier,
    ],
  };

  const queryKey = queryPageKeys[page];

  const queryFn = async () => {
    switch (page) {
      case 'status-and-review': {
        const { data } = await typedApiClient.v1.boltAuditStatusAndReviewList(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        );
        return normalizeData(data, page, category);
      }
      case 'summary-data': {
        const { data } =
          await typedApiClient.v1.boltAuditGetBoltAuditSummaryScreen({
            obfuscatedLoanIdentifier,
            page: pageEnum as unknown as DtoV1EnumBoltAuditPage,
          });
        return normalizeData(data, page, category);
      }
      case 'borrower1003': {
        const { data } = await typedApiClient.v1.boltAuditBorrower1003List(
          obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
          obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        );
        return normalizeData(data, page, category);
      }
    }
  };

  type ParsedCaivrsDebtEntry = {
    caseNumber?: string | null;
    fields?:
      | KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse[]
      | null;
    CaseNumber?: string | null;
    Fields?:
      | KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse[]
      | null;
  };

  const normalizeCaivrsDebtFieldEntries = (
    entries: DtoV1BoltAuditEntityFieldResponse[] | null | undefined,
  ) =>
    (entries ?? []).map((entry) => ({
      fieldValue:
        entry.fieldValue ??
        (entry as { FieldValue?: string[] | null }).FieldValue ??
        [],
      userName:
        entry.userName ??
        (entry as { UserName?: string | null }).UserName ??
        null,
      modifiedDateTime:
        entry.modifiedDateTime ??
        (entry as { ModifiedDateTime?: string }).ModifiedDateTime,
    }));

  const normalizeCaivrsDebtFields = (
    debtEntry: ParsedCaivrsDebtEntry,
  ): KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse[] =>
    (debtEntry.fields ?? debtEntry.Fields ?? []).map((field) => ({
      key: field.key ?? (field as { Key?: string | null }).Key ?? null,
      value: normalizeCaivrsDebtFieldEntries(
        field.value ??
          (field as { Value?: DtoV1BoltAuditEntityFieldResponse[] | null })
            .Value,
      ),
    }));

  const parseCaivrsDebtEntries = (serializedDebtEntry: string) => {
    try {
      const parsed = JSON.parse(serializedDebtEntry) as
        | ParsedCaivrsDebtEntry[]
        | undefined;

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.map((debtEntry) => ({
        caseNumber: debtEntry.caseNumber ?? debtEntry.CaseNumber ?? null,
        fields: normalizeCaivrsDebtFields(debtEntry),
      }));
    } catch {
      return [];
    }
  };

  const getCaivrsDebtEntries = (
    sourceFields: KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse[],
  ) =>
    sourceFields
      .filter((field) => field.key === 'Debt Entries')
      .flatMap((field) => field.value ?? [])
      .flatMap((value) => value.fieldValue ?? [])
      .flatMap(parseCaivrsDebtEntries);

  return useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
  });

  function normalizeData(data, page, category): NormalizedAuditEntity[] {
    if (!data) return [];
    switch (page) {
      case 'status-and-review':
        return (
          (data as DtoV1BoltAuditBoltAuditResponse).entitiesAudit || []
        ).map((e) => ({
          sectionTitle: e.sectionTitle ?? '',
          entityFields: e.fieldsAudit ?? [],
          useAccordions: false,
        }));
      case 'summary-data': {
        const summaryData =
          data as DtoV1BoltAuditBoltAuditSummaryScreenResponse;

        if (pageEnum === AuditHistoryPageEnum.VA_CAIVRS.toString()) {
          return (summaryData?.activeEntities || []).flatMap((entity) => {
            const borrowerTab = entity.entityDisplayName || 'CAIVRS';
            const sourceFields = entity.entityFields ?? [];
            const authorizationFields = sourceFields.filter(
              (field) => field.key === 'CAIVRS Number',
            );
            const debtEntries = getCaivrsDebtEntries(sourceFields);

            const fieldGroups = [
              {
                title: 'Authorization Number',
                fields: authorizationFields,
              },
              ...debtEntries.map((debt, index) => ({
                title: `Debt Information - ${debt.caseNumber || index + 1}`,
                fields: debt.fields ?? [],
              })),
            ];

            return fieldGroups
              .filter((group) => group.fields.length > 0)
              .map((group) => ({
                tab: 'Active',
                sectionTitle: borrowerTab,
                entityFields: group.fields,
                entityDisplayName: group.title,
                useAccordions: true,
                isActiveDeletedTab: false,
              }));
          });
        }

        const normalizeEntity = (
          e: DtoV1BoltAuditBoltAuditEntity,
          tab?: string,
          isActiveDeletedTab: boolean = true,
          useAccordions: boolean = true,
        ): NormalizedAuditEntity => ({
          tab: tab,
          sectionTitle: buildSectionTitle(
            tab || '',
            page || '',
            category || '',
          ),
          entityFields: e.entityFields ?? [],
          entityDisplayName: e.entityDisplayName,
          deletedByDisplayName: e.deletedByDisplayName,
          useAccordions: useAccordions,
          isActiveDeletedTab: isActiveDeletedTab,
        });
        const active = (summaryData?.activeEntities || []).map((e) =>
          normalizeEntity(e, 'Active'),
        );
        const deleted = (summaryData?.deletedEntities || []).map((e) =>
          normalizeEntity(e, 'Deleted'),
        );
        const reserves = (summaryData?.reserveEntities || []).map((e) =>
          normalizeEntity(e, 'Reserves'),
        );
        return [...active, ...deleted, ...reserves];
      }
      case 'borrower1003': {
        const borrowerData =
          data as DtoV1BoltAuditBorrower1003AuditPageResponse;
        const normalizeEntity = (
          e: DtoV1BoltAuditBoltAuditEntity,
          entitySectionTitle?: string,
          tab?: string,
          displayName?: string,
          useAccordians: boolean = true,
        ): NormalizedAuditEntity => ({
          sectionTitle: tab,
          entityFields: e.entityFields ?? [],
          entitySectionTitle: entitySectionTitle,
          entityDisplayName: displayName,
          useAccordions: useAccordians,
        });

        const borrowerDataTab = (borrowerData?.borrowerAuditData || []).flatMap(
          (borrower) =>
            mapBorrowerAuditEntites(
              'BORROWER DATA',
              borrower.borrowerName,
              borrower.entitiesAudit || [],
              normalizeEntity,
            ),
        );
        const residentialHistoryTab = (
          borrowerData?.borrowerResidencyHistoryAuditData || []
        ).flatMap((borrowerResidencyHistory) =>
          mapBorrowerAuditEntites(
            'ADDRESS INFO',
            borrowerResidencyHistory.borrowerName,
            borrowerResidencyHistory.entitiesAudit || [],
            normalizeEntity,
          ),
        );
        const declarationsTab = (
          borrowerData?.borrowerDeclarationsAuditData || []
        ).flatMap((borrowerDeclarations) =>
          mapBorrowerAuditEntites(
            'DECLARATIONS INFO',
            borrowerDeclarations.borrowerName,
            borrowerDeclarations.entitiesAudit || [],
            normalizeEntity,
          ),
        );

        return [
          ...borrowerDataTab,
          ...residentialHistoryTab,
          ...declarationsTab,
        ];
      }
    }
  }

  function buildSectionTitle(tab: string, pageId: string, categoryId: string) {
    const pageNum = Number.parseInt(pageId);
    const isPageANumber = !Number.isNaN(pageNum);
    const categoryName = getCategoryName(pageNum);

    const sectionTitle =
      !isPageANumber && categoryName !== null
        ? categoryName
        : categoryId || pageId;

    const tabName =
      tab === 'Reserves'
        ? 'Reserves'
        : `${tab} ${toTitleCase(sectionTitle.replaceAll('-', ' '))}`;

    return tabName;
  }

  function mapBorrowerAuditEntites(
    sectionTitle,
    borrowerName,
    entities,
    normalizeEntity,
  ) {
    // Group entities by their sectionTitle
    const entitiesBySectionTitle = groupEntitiesBySectionTitle(entities);

    // For each unique sectionTitle, consolidate all fields
    const mappedEntities = Object.entries(entitiesBySectionTitle).map(
      ([entitySectionTitle, entitiesGroup]) => {
        // Collect and group all fields from all entities with this sectionTitle
        const allGroupedFields = groupFieldsBySectionTitle(entitiesGroup);
        const tableSectionTitle = resolveSectionTitle(
          entitySectionTitle,
          sectionTitle,
        );

        // Create a single normalized entity for this sectionTitle with all consolidated fields
        return normalizeEntity(
          {
            entityDisplayName: sectionTitle,
            entityFields: Object.values(allGroupedFields),
          },
          tableSectionTitle,
          sectionTitle,
          borrowerName,
          true,
        );
      },
    );

    return mappedEntities;
  }

  function groupEntitiesBySectionTitle(entities: NormalizedAuditEntity[]) {
    return entities.reduce(
      (acc, entity) => {
        const key = entity.sectionTitle || entity.entityDisplayName;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(entity);
        return acc;
      },
      {} as Record<string, DtoV1BoltAuditBorrower1003EntityAudit[]>,
    );
  }

  function groupFieldsBySectionTitle(
    entitiesGroup: DtoV1BoltAuditBorrower1003EntityAudit[],
  ) {
    return entitiesGroup?.reduce(
      (fieldsByKey, entity) => {
        entity.fieldsAudit?.forEach((field) => {
          if (!fieldsByKey[field.key]) {
            fieldsByKey[field.key] = createEmptyField(field.key);
          }

          const transformValues = field.value.map(transformFieldValue);
          fieldsByKey[field.key].value.push(...transformValues);
        });

        return fieldsByKey;
      },
      {} as Record<
        string,
        KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse
      >,
    );
  }

  function createEmptyField(
    key: string,
  ): KeyValuePairSystemStringICollectionDtoV1BoltAuditEntityFieldResponse {
    return {
      key,
      value: [] as DtoV1BoltAuditEntityFieldResponse[],
    };
  }

  function transformFieldValue(
    val: DtoV1BoltAuditFieldAudit,
  ): DtoV1BoltAuditEntityFieldResponse {
    return {
      fieldValue: [val.fieldValue],
      userName: val.userName,
      modifiedDateTime: val.modifiedDateTime,
    };
  }

  function resolveSectionTitle(
    entitySectionTitle: string,
    defaultTitle: string,
  ): string {
    const title =
      entitySectionTitle === 'undefined' ? defaultTitle : entitySectionTitle;
    return toTitleCase(title.toString());
  }
}

export function useGetAssetBankStatmentAuditHistory(assetId, documentId) {
  const { obfuscatedLoanIdentifier } = useUrl();
  return useQuery({
    queryKey: [
      'audit-history',
      'getAssetBankStatmentAuditHistory',
      obfuscatedLoanIdentifier,
    ],
    queryFn: async () => {
      const { data } = await typedApiClient.v1.boltAuditAccountStatementDetail(
        obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId,
        obfuscatedLoanIdentifier.obfuscatedLoanRecordId,
        assetId,
        documentId,
      );

      return data.entity;
    },
  });
}

export const useGetMortgageHistoryAuditData = (
  pageEnum: string,
  entityEnum: string,
  references: Record<string, string>,
) => {
  const { obfuscatedLoanIdentifier } = useUrl();

  return useQuery({
    queryKey: [
      'mortgageHistoryAuditData',
      'getSectionedAuditHistoryData',
      { entityEnum, pageEnum, references },
    ],
    queryFn: async () => {
      const requestBody: DtoV1BoltAuditBoltAuditRequest = {
        obfuscatedLoanIdentifier,
        page: Number.parseInt(pageEnum) as DtoV1EnumBoltAuditPage,
      };

      if (entityEnum) {
        requestBody.entityReferenceAssociations = [
          {
            entity: Number.parseInt(entityEnum) as DtoV1EnumEntityType,
            references: references,
          },
        ];
      }
      const { data } =
        await typedApiClient.v1.boltAuditGetSectionedBoltAudit(requestBody);

      // Normalize sectioned response so sections/fields match expected client shape
      const transformed = transformSectionedBoltAuditEndpointData(data);

      return transformed?.entity ?? [];
    },
  });
};
